import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  useMediaQuery,
  Link,
  Breadcrumbs,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

/**
 * Privacy Policy page component
 * 
 * Contains the full privacy policy for the MyEventShop platform
 * detailing how user data is collected, processed, stored and protected
 */
const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };
  
  // Last Updated date
  const lastUpdated = "April 5, 2025";

  // Table of contents items
  const tableOfContents = [
    { title: '1. Introduction', id: 'introduction' },
    { title: '2. Information We Collect', id: 'information-collection' },
    { title: '3. How We Use Your Information', id: 'information-usage' },
    { title: '4. How We Share Your Information', id: 'information-sharing' },
    { title: '5. Cookies and Tracking Technologies', id: 'cookies' },
    { title: '6. Data Retention', id: 'data-retention' },
    { title: '7. Your Privacy Rights', id: 'privacy-rights' },
    { title: '8. Data Security', id: 'data-security' },
    { title: '9. Children\'s Privacy', id: 'childrens-privacy' },
    { title: '10. International Data Transfers', id: 'international-transfers' },
    { title: '11. Changes to This Privacy Policy', id: 'changes' },
    { title: '12. Contact Us', id: 'contact' }
  ];

  return (
    <Box>
      <AppMenu 
                onLoginClick={() => setIsLoginOpen(true)}
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mt: 3, mb: 2 }}
        >
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer' }}
          >
            Home
          </Link>
          <Typography color="text.primary">Privacy Policy</Typography>
        </Breadcrumbs>

        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            Privacy Policy
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Last Updated: {lastUpdated}
          </Typography>
        </Box>

        {/* Main Content Container */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 8 }}>
          {/* Table of Contents */}
          <Box 
            component={Paper} 
            elevation={2}
            sx={{
              p: 3, 
              borderRadius: 2, 
              width: { xs: '100%', md: '300px' },
              flexShrink: 0,
              position: { xs: 'static', md: 'sticky' },
              top: '20px',
              alignSelf: 'flex-start',
              maxHeight: { xs: 'auto', md: 'calc(100vh - 40px)' },
              overflowY: { xs: 'visible', md: 'auto' }
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>Table of Contents</Typography>
            <List sx={{ p: 0 }}>
              {tableOfContents.map((item) => (
                <ListItem 
                  key={item.id} 
                  disablePadding 
                  sx={{ mb: 1 }}
                >
                  <ListItemText 
                    primary={
                      <Link 
                        href={`#${item.id}`} 
                        underline="hover" 
                        color="text.primary"
                        sx={{ 
                          display: 'block',
                          fontSize: '0.9rem',
                          lineHeight: 1.4,
                        }}
                      >
                        {item.title}
                      </Link>
                    } 
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Privacy Policy Content */}
          <Box 
            component={Paper}
            elevation={2}
            sx={{ 
              p: { xs: 3, md: 4 }, 
              borderRadius: 2,
              flex: '1 1 auto',
            }}
          >
            <Box id="introduction" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                1. Introduction
              </Typography>
              <Typography variant="body1" paragraph>
                This Privacy Policy explains how MyEventShop ("we," "our," or "us") collects, uses, shares, and protects information about you when you use our website, mobile applications, and other online products and services (collectively, the "Services").
              </Typography>
              <Typography variant="body1" paragraph>
                By using our Services, you agree to the collection, use, disclosure, and procedures this Privacy Policy describes. Beyond the Privacy Policy, your use of our Services is also subject to our Terms of Service.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="information-collection" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                2. Information We Collect
              </Typography>
              <Typography variant="body1" paragraph>
                We collect information directly from you when you provide it to us, automatically when you use our Services, and from third parties.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                A. Information You Provide to Us
              </Typography>
              <Typography variant="body1" paragraph>
                We collect information you provide directly to us when you:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Create an account (name, email address, password)</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Complete your profile (profile photo, bio, location, preferences)</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Create or manage events (event details, descriptions, images)</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Purchase tickets (payment information, billing address)</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Communicate with us (questions, feedback, reviews)</Typography>
                </ListItem>
              </List>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                B. Information We Collect Automatically
              </Typography>
              <Typography variant="body1" paragraph>
                When you use our Services, we automatically collect:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Device information (IP address, browser type, operating system)</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Usage data (pages viewed, time spent, links clicked)</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Location information (with your permission)</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Cookies and similar tracking technologies</Typography>
                </ListItem>
              </List>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                C. Information We Receive from Third Parties
              </Typography>
              <Typography variant="body1" paragraph>
                We may receive information about you from:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Social media platforms when you connect your accounts</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Business partners we work with for events</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Payment processors for transaction information</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="information-usage" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                3. How We Use Your Information
              </Typography>
              <Typography variant="body1" paragraph>
                We use the information we collect to:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Provide, maintain, and improve our Services</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Process transactions and send related information</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Send you technical notices, updates, security alerts, and support messages</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Respond to your comments, questions, and customer service requests</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Communicate with you about products, services, and events</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Personalize your experience and deliver content relevant to your interests</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Monitor and analyze trends, usage, and activities in connection with our Services</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Detect, investigate, and prevent fraudulent transactions and other illegal activities</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Carry out any other purpose described to you at the time the information was collected</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="information-sharing" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                4. How We Share Your Information
              </Typography>
              <Typography variant="body1" paragraph>
                We may share information about you as follows:
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                A. With Other Users
              </Typography>
              <Typography variant="body1" paragraph>
                When you create events or interact with the Services, certain information may be visible to other users, such as your profile information, events you've created, and your public activity.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                B. With Service Providers
              </Typography>
              <Typography variant="body1" paragraph>
                We share information with vendors, service providers, and consultants that need access to your information to provide services to us, such as companies that assist us with web hosting, payment processing, fraud prevention, customer service, and marketing.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                C. For Legal Reasons
              </Typography>
              <Typography variant="body1" paragraph>
                We may share information if we believe it's required to comply with applicable laws, regulations, legal processes, or governmental requests, to protect the rights, property, and safety of MyEventShop, our users, or others.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                D. During Business Transfers
              </Typography>
              <Typography variant="body1" paragraph>
                We may share information in connection with a merger, sale of company assets, financing, or acquisition of all or a portion of our business.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                E. With Your Consent
              </Typography>
              <Typography variant="body1" paragraph>
                We may share information with your consent or at your direction, including if you authorize a connection with a third-party service.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="cookies" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                5. Cookies and Tracking Technologies
              </Typography>
              <Typography variant="body1" paragraph>
                We and our third-party partners use cookies, web beacons, and similar tracking technologies to track user activity and collect data about our website visitors. Cookies are small text files that web servers place on your device to help remember information about your visit.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                A. Types of Cookies We Use
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Essential cookies:</strong> Necessary for the website to function properly</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Analytics cookies:</strong> Help us understand how visitors interact with our website</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Functionality cookies:</strong> Remember choices you make to provide enhanced features</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Advertising cookies:</strong> Collect information to provide targeted advertising</Typography>
                </ListItem>
              </List>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                B. Your Cookie Choices
              </Typography>
              <Typography variant="body1" paragraph>
                Most web browsers are set to accept cookies by default. If you prefer, you can usually set your browser to remove or reject cookies. Please note that removing or rejecting cookies could affect the availability and functionality of our Services.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="data-retention" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                6. Data Retention
              </Typography>
              <Typography variant="body1" paragraph>
                We store the information we collect about you for as long as is necessary for the purpose(s) for which we originally collected it or for other legitimate business purposes, including to meet our legal, regulatory, or other compliance obligations.
              </Typography>
              <Typography variant="body1" paragraph>
                When determining the retention period for your data, we consider:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">The amount, nature, and sensitivity of the information</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">The potential risk of harm from unauthorized use or disclosure</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">The purposes for which we process the information</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Whether we can achieve those purposes through other means</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Legal and regulatory requirements</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="privacy-rights" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                7. Your Privacy Rights
              </Typography>
              <Typography variant="body1" paragraph>
                Depending on your location, you may have certain rights regarding your personal information:
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                A. Access and Portability
              </Typography>
              <Typography variant="body1" paragraph>
                You may request access to the personal information we hold about you and request a copy of your data in a structured, machine-readable format.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                B. Correction
              </Typography>
              <Typography variant="body1" paragraph>
                You may request that we correct inaccurate or incomplete information about you.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                C. Deletion
              </Typography>
              <Typography variant="body1" paragraph>
                You may request that we delete your personal information in certain circumstances.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                D. Restriction and Objection
              </Typography>
              <Typography variant="body1" paragraph>
                You may request that we restrict processing of your personal information or object to our processing of your information.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                E. Exercising Your Rights
              </Typography>
              <Typography variant="body1" paragraph>
                To exercise these rights, please contact us at privacy@myeventshop.com. We may need to verify your identity before responding to your request.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="data-security" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                8. Data Security
              </Typography>
              <Typography variant="body1" paragraph>
                We take reasonable measures to help protect information about you from loss, theft, misuse, unauthorized access, disclosure, alteration, and destruction. However, no security system is impenetrable, and we cannot guarantee the security of our systems.
              </Typography>
              <Typography variant="body1" paragraph>
                Our security measures include:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Encryption of data in transit using SSL/TLS</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Regular security assessments and testing</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Access controls for our systems and data</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Employee training on security and privacy practices</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="childrens-privacy" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                9. Children's Privacy
              </Typography>
              <Typography variant="body1" paragraph>
                Our Services are not directed to children under 16. We do not knowingly collect personal information from children under 16. If we learn that we have collected personal information of a child under 16, we will take steps to delete such information as quickly as possible. If you believe a child under 16 has provided us with personal information, please contact us at privacy@myeventshop.com.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="international-transfers" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                10. International Data Transfers
              </Typography>
              <Typography variant="body1" paragraph>
                MyEventShop is based in Ireland, and we process and store information in various locations depending on where our service providers operate. If you are located outside of Ireland, your information may be transferred to, stored, and processed in countries where the privacy laws may not be as protective as those in your jurisdiction.
              </Typography>
              <Typography variant="body1" paragraph>
                If we transfer your information to countries outside the European Economic Area (EEA) or other regions with comprehensive data protection laws, we will ensure that the transfer complies with data protection law and that there are appropriate safeguards in place to protect your personal information.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="changes" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                11. Changes to This Privacy Policy
              </Typography>
              <Typography variant="body1" paragraph>
                We may update this Privacy Policy from time to time in response to changing legal, technical, or business developments. When we update our Privacy Policy, we will take appropriate measures to inform you, consistent with the significance of the changes we make.
              </Typography>
              <Typography variant="body1" paragraph>
                You can see when this Privacy Policy was last updated by checking the "Last Updated" date displayed at the top of this Privacy Policy. Your continued use of our Services after the revised Privacy Policy has become effective indicates that you have read, understood, and agreed to the current version.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="contact" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                12. Contact Us
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </Typography>
              <Typography variant="body1" paragraph>
                MyEventShop<br />
                123 Barrow Street<br />
                Dublin, DO4 V4X7<br />
                Ireland<br /><br />
                Email: privacy@myeventshop.com<br />
                Phone: +353 (55) 123-4567
              </Typography>
              <Typography variant="body1" paragraph>
                For EU/EEA residents: You have the right to lodge a complaint with your local data protection authority if you believe that our processing of your personal information does not comply with applicable data protection law.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Call to Action */}
        <Paper 
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            mb: 8,
            bgcolor: 'rgba(95, 60, 111, 0.05)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" component="p" mb={3}>
            Have questions about our Privacy Policy? We're here to help.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/contact')}
            sx={{
              px: 4,
              py: 1,
              fontWeight: 'medium',
            }}
          >
            Contact Us
          </Button>
        </Paper>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default PrivacyPolicy; 